import { useCallback, useState } from 'react';
import dayjs from 'dayjs';
import type { TObjectExportJob } from 'types/objectJob.type';
import { errorNotification } from 'utils/notifications';
import { Tooltip, Typography } from '@timeedit/ui-components';
import { Button, Spin } from '@timeedit/ui-components/antd';
import { useOperationPermission } from '../../../../hooks/useOperationPermission';
import { useLazyExportJobDownloadURLQuery } from '../../../export/api/exportObjectsApi';
import useErrorTableData from '../hooks/useErrorTableData';
import JobErrorTable from './JobErrorTable';
import './JobExecutionResult.scss';

type Props = {
  job: TObjectExportJob;
};

type TooltipState = {
  fileExpired: boolean;
  inProgress: boolean;
  hasExportPermission: boolean;
  hasDownloadUrl: boolean;
  hasError: boolean;
};

const getTooltipTitle = (state: TooltipState) => {
  if (state.hasError) {
    return 'There was a problem with the export';
  }

  if (state.fileExpired) {
    return 'The export file has expired and can no longer be downloaded.';
  }

  if (state.inProgress) {
    return 'The export is still in progress. The download will be available once the export is finished.';
  }

  if (!state.hasDownloadUrl) {
    return 'Download URL is not available yet. Please try again in a moment.';
  }

  if (!state.hasExportPermission) {
    return 'You do not have permission to download this export.';
  }

  return undefined;
};

const ExportJobExecutionResult: React.FC<Props> = (props: Props) => {
  const inProgress = ['QUEUED', 'RUNNING'].includes(props.job.status);
  const hasExportPermission = useOperationPermission('EXPORT');
  const today = dayjs();
  const fileExpired =
    !!props.job.fileExpires && dayjs(props.job.fileExpires).isBefore(today);
  const hasDownloadUrl = !!props.job.fileExpires;
  const downloadDisabled = inProgress || fileExpired || !hasDownloadUrl;

  const [fetchDownloadLink] = useLazyExportJobDownloadURLQuery();
  const [isDownloading, setIsDownloading] = useState(false);

  const summary = props.job.summary;

  const errorData = useErrorTableData(props.job);

  const tooltipTitle = getTooltipTitle({
    hasError: props.job.status === 'FAILED',
    fileExpired,
    inProgress,
    hasExportPermission,
    hasDownloadUrl,
  });

  const onDownload = useCallback(async () => {
    try {
      setIsDownloading(true);
      const { url } = await fetchDownloadLink({ id: props.job._id }).unwrap();

      const link = document.createElement('a');
      link.href = url;

      link.setAttribute('download', props.job.fileName);
      link.target = '_blank';
      link.rel = 'noopener noreferrer';

      link.click();
    } catch (error) {
      errorNotification('Error downloading file', [
        'An error occurred while downloading the exported file. Please reload the page and try again.',
      ]);
    } finally {
      setIsDownloading(false);
    }
  }, [fetchDownloadLink, props.job.fileName, props.job._id]);

  return (
    <>
      <div className="job-execution-result-title">
        <Typography.Title level={4}>Result</Typography.Title>{' '}
        <Tooltip title={tooltipTitle}>
          <Button
            className="job-execution-result-csv-download-button"
            disabled={downloadDisabled || !hasExportPermission || isDownloading}
            onClick={onDownload}
          >
            Download (.{props.job.format})
          </Button>
        </Tooltip>
      </div>

      {inProgress && (
        <div className="job-execution-result-box">
          <Spin /> <Typography.Text>Export in progress</Typography.Text>
        </div>
      )}
      {!inProgress && (
        <div className="job-execution-result-box">
          <table>
            <tbody>
              <tr>
                <td>
                  <Typography.Text>Exported objects:</Typography.Text>
                </td>
                <td>
                  <Typography.Text strong>
                    {summary.successful}/{summary.total}
                  </Typography.Text>
                </td>
              </tr>
              <tr>
                <td>
                  <Typography.Text>Expiration date:</Typography.Text>
                </td>
                <td>
                  <Typography.Text strong>
                    {!!props.job.fileExpires &&
                      dayjs(props.job.fileExpires).format(
                        'YYYY-MM-DD HH:mm:ss'
                      )}
                  </Typography.Text>
                </td>
              </tr>
              <tr>
                <td>
                  <Typography.Text>Failed exports:</Typography.Text>
                </td>
                <td>
                  <Typography.Text strong>{summary.failed}</Typography.Text>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <JobErrorTable data={errorData} title="Failed exports" />
    </>
  );
};

export default ExportJobExecutionResult;
